import Accordion from "components/blocks/Accordion";
import AccordionItem from "components/blocks/Accordion/AccordionItem";
import LoadingOverlay from "components/blocks/LoadingOverlay";
import { useQuery } from "hooks/useQuery";
import { useTranslation } from "react-i18next";
import { CourseOrderLocalItemResult } from "services/courseOrder/models/CourseOrderType";
import { ScientificPublicationsService } from "services/scientificPublicationsService";
import { FileCard } from "views/welcomePage/components/PublicationsGallery/PublicationsDetails";
import { useState } from "react";
import useMutation from "hooks/useMutation";
import { FileService } from "services/fileService";
import Modal from "components/blocks/Modal";
import { ScientificPublishingAttachment } from "views/home/scientificPublication/form/ScientificPublicationForm.types";

const ScientificPublication = ({ courseOrder }: { courseOrder: CourseOrderLocalItemResult | null }) => {
  const { t } = useTranslation("Orders");
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    file: ScientificPublishingAttachment | null;
  }>({
    isOpen: false,
    file: null,
  });

  const { data: scientificPublication, loading: courseLoading } = useQuery({
    queryFn: async () => {
      // TODO: need Scientific PublicationBy Id
      return await ScientificPublicationsService.getScientificPublicationById(4);
    },
    options: { enabled: !!courseOrder },
    triggers: [courseOrder],
  });

  // File download mutation
  const { mutateAsync: downloadFile, loading: fileDownloadLoading } = useMutation({
    queryFn: async ({ id, fileName }: { id: string; fileName: string }) => {
      await FileService.downloadFile(id, fileName);
      return { hasError: false, data: null, description: "", code: 200 };
    },
  });

  const handleFileDownload = (fileId: string, fileName: string) => {
    if (fileDownloadLoading) return;
    downloadFile({ id: fileId, fileName });
  };

  const handleFilePreview = (file: ScientificPublishingAttachment) => {
    setPreviewModal({
      isOpen: true,
      file: file,
    });
  };

  const handleClosePreview = () => {
    setPreviewModal({
      isOpen: false,
      file: null,
    });
  };

  return (
    <>
      <Accordion>
        <AccordionItem initiallyOpen title={t("scientificPublication")} elementId={"ScientificPublication"}>
          <div className="position-relative p-0 m-0">
            <LoadingOverlay visible={courseLoading} />
            <div className="d-flex flex-wrap gap-3">
              {scientificPublication?.scientificPublishingAttachments
                ? scientificPublication?.scientificPublishingAttachments.map((ele, index) => {
                    return (
                      <FileCard
                        key={index}
                        file={ele}
                        onDownload={handleFileDownload}
                        onPreview={handleFilePreview}
                        isDownloading={fileDownloadLoading}
                      />
                    );
                  })
                : t("Common:noData")}
            </div>
          </div>

          <div className="divider mt-4 "></div>
        </AccordionItem>
      </Accordion>

      {/* File Preview Modal */}
      {previewModal.file && previewModal.file.file && (
        <Modal
          isOpen={previewModal.isOpen}
          onClose={handleClosePreview}
          size="lg"
          title={previewModal.file.file.name || ""}
        >
          <div className="p-4">
            <div className="modal-body" style={{ maxHeight: "70vh", overflow: "auto" }}>
              {previewModal.file.file.extension?.toLowerCase().replace(/^\./, "") === "pdf" ? (
                <div style={{ width: "100%", height: "600px" }}>
                  <iframe
                    src={`${window.location.origin}/api/FileManager/PreviewFile/${previewModal.file.file.id}#toolbar=0&navpanes=0&scrollbar=1`}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    title={previewModal.file.translatedName}
                  />
                </div>
              ) : ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "tiff", "tif"].includes(
                  previewModal.file.file.extension?.toLowerCase().replace(/^\./, "") || ""
                ) ? (
                <div className="text-center">
                  <img
                    src={`${window.location.origin}/api/FileManager/PreviewFile/${previewModal.file.file.id}`}
                    alt={previewModal.file.translatedName}
                    style={{ maxWidth: "100%", maxHeight: "600px", objectFit: "contain" }}
                  />
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center gap-4 py-5">
                  <span className={`icon-document fs-1`} style={{ fontSize: "120px" }} />
                  <div className="alert alert-info w-100">
                    <span className="icon-info-circle me-2" />
                    {t("Common:downloadFileToView")}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ScientificPublication;
